import { useMemo, useState } from "react";
import { useParams } from "react-router-dom";
import {
  CartesianGrid,
  Line,
  LineChart,
  ResponsiveContainer,
  Tooltip,
  XAxis,
  YAxis,
} from "recharts";
import { api, microsToCredits } from "../api";
import { useResource } from "../resources";
import TxRow from "../components/TxRow";
import RecordStatus from "../components/RecordStatus";

export default function AccountPage() {
  const { id } = useParams<{ id: string }>();
  const resource = useResource(id, api.account);
  const data = resource.data;
  const status = <RecordStatus resource={resource} />;
  const visit = useMemo(() => ({ id }), [id]);
  const [pagination, setPagination] = useState({ visit, page: 0 });
  const PAGE_SIZE = 20;
  const lastPage = Math.max(0, Math.ceil((data?.recent_txs.length ?? 0) / PAGE_SIZE) - 1);
  const page = pagination.visit === visit ? Math.min(pagination.page, lastPage) : 0;
  const setPage = (update: (page: number) => number) => setPagination({ visit, page: update(page) });

  if (!data) return status;

  const chart = data.history.map((h) => ({
    round: h.round,
    credits: Number(BigInt(h.balance_micros) / 1000n) / 1000,
  }));
  const txPage = data.recent_txs.slice(page * PAGE_SIZE, (page + 1) * PAGE_SIZE);

  return (
    <div className="space-y-5">
      {status}
      <div>
        <h1 className="text-xl font-bold text-mesh-accent">account</h1>
        <div className="hex text-mesh-mute text-sm break-all">{id}</div>
      </div>
      <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
        <div className="bg-mesh-panel rounded-lg p-4">
          <div className="text-xs text-mesh-mute uppercase">balance</div>
          <div className="text-2xl font-bold">
            {microsToCredits(data.balance_micros)} cr
          </div>
        </div>
        <div className="bg-mesh-panel rounded-lg p-4">
          <div className="text-xs text-mesh-mute uppercase">tx count</div>
          <div className="text-2xl font-bold">
            {data.account?.tx_count ?? 0}
          </div>
        </div>
        <div className="bg-mesh-panel rounded-lg p-4">
          <div className="text-xs text-mesh-mute uppercase">credit limit</div>
          <div className="text-2xl font-bold">
            {data.account
              ? microsToCredits(data.account.credit_limit_micros)
              : "—"}{" "}
            cr
          </div>
        </div>
      </div>

      <section className="bg-mesh-panel/40 rounded-lg p-4">
        <h2 className="text-sm uppercase tracking-wider text-mesh-mute mb-3">
          balance over rounds
        </h2>
        {chart.length === 0 ? (
          <div className="text-mesh-mute text-sm py-6 text-center">
            no balance history yet
          </div>
        ) : (
          <div style={{ width: "100%", height: 240 }}>
            <ResponsiveContainer>
              <LineChart data={chart}>
                <CartesianGrid stroke="#1c2746" strokeDasharray="3 3" />
                <XAxis dataKey="round" stroke="#7c87a4" />
                <YAxis stroke="#7c87a4" />
                <Tooltip
                  contentStyle={{
                    background: "#121a33",
                    border: "1px solid #1c2746",
                  }}
                />
                <Line
                  type="monotone"
                  dataKey="credits"
                  stroke="#3aa6ff"
                  strokeWidth={2}
                  dot={{ r: 3 }}
                />
              </LineChart>
            </ResponsiveContainer>
          </div>
        )}
      </section>

      <section className="bg-mesh-panel/40 rounded-lg p-4">
        <h2 className="text-sm uppercase tracking-wider text-mesh-mute mb-3">
          recent txs ({data.recent_txs.length})
        </h2>
        {txPage.map((t) => (
          <TxRow key={t.tx_id} tx={t} />
        ))}
        {data.recent_txs.length > PAGE_SIZE && (
          <div className="flex justify-between mt-3 text-sm">
            <button
              onClick={() => setPage((p) => Math.max(0, p - 1))}
              disabled={page === 0}
              className="px-3 py-1 bg-mesh-panel rounded disabled:opacity-40"
            >
              prev
            </button>
            <span className="text-mesh-mute">
              page {page + 1} /{" "}
              {Math.ceil(data.recent_txs.length / PAGE_SIZE)}
            </span>
            <button
              onClick={() =>
                setPage((p) =>
                  (p + 1) * PAGE_SIZE >= data.recent_txs.length ? p : p + 1,
                )
              }
              disabled={(page + 1) * PAGE_SIZE >= data.recent_txs.length}
              className="px-3 py-1 bg-mesh-panel rounded disabled:opacity-40"
            >
              next
            </button>
          </div>
        )}
      </section>
    </div>
  );
}
